import React from "react";
import { motion } from "framer-motion";
import {
  Star, CaretRight, ArrowSquareOut, Lightning, Brain, Coffee,
  Flag, Triangle, Rocket, ThumbsUp, ArrowsClockwise, Warning,
} from "@phosphor-icons/react";
import { tradingViewStockUrl, screenerStockUrl } from "../../lib/tradingview";

/**
 * Universal compact stock row used across ALL Dashboard scanner tabs.
 *
 * Layout (single horizontal line, left → right):
 *   ⭐  SYMBOL ↗   COMPANY        TYPE BADGE   ₹PRICE   +PCT%TODAY   SCORE   ENTRY · SL · TARGET   ⋯
 *
 * Click anywhere (except the explicit interactive icons) opens the
 * StockDetailModal via `onOpen`. Star toggles watchlist. TV/SCR open
 * deep links in a new tab.
 *
 * Props:
 *   - item: stock dict (symbol, last_price, entry, stop_loss, target, etc.)
 *   - scoreKey: which field is the headline score (default "score")
 *   - scoreLabel: short label for the score chip (default "")
 *   - typeBadge: { label, tone: "pos" | "mod" | "neg" | "muted" } (optional)
 *   - onOpen(item): called on row click — opens StockDetailModal
 *   - onWatch({symbol}): toggles watchlist; omit to hide star
 *   - watched: boolean
 *   - testIdPrefix: e.g. "row-strongest" — gets {symbol} suffix
 */
const fmt = (n, d = 2) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d })
    : "—";

const TONE_CLASS = {
  pos: "text-pos border-pos/40 bg-pos-soft/40",
  mod: "text-mod border-mod/40 bg-mod-soft/40",
  neg: "text-neg border-neg/40 bg-neg-soft/40",
  muted: "text-secondary-fg border-subtle",
};

const KIND_ICON = {
  cup_short: Coffee, cup_long: Coffee,
  flag: Flag, asc_triangle: Triangle,
  approaching: Rocket, strongest: Lightning, power: ThumbsUp,
  retest: ArrowsClockwise,
};

function scoreTone(s) {
  if (s == null) return "muted";
  if (s >= 8) return "pos";
  if (s >= 6) return "mod";
  return "neg";
}

/** Tiny badge that says how old a breakout signal is. */
function SignalAgeBadge({ detectedAt }) {
  if (!detectedAt) return null;
  let days = 0;
  try {
    const ms = Date.now() - new Date(detectedAt).getTime();
    if (!isFinite(ms) || ms < 0) return null;
    days = Math.floor(ms / (24 * 60 * 60 * 1000));
  } catch {
    return null;
  }
  const label =
    days <= 0 ? "TODAY" :
    days === 1 ? "1d old" :
    days < 7 ? `${days}d old` :
    days < 30 ? `${Math.floor(days / 7)}w old` :
    `${Math.floor(days / 30)}mo old`;
  // Fresh signals get a green tint; aging ones go muted.
  const tone =
    days <= 0 ? "border-pos/50 bg-pos-soft/30 text-pos" :
    days <= 3 ? "border-pos/30 bg-pos-soft/15 text-pos/90" :
    days <= 14 ? "border-subtle text-secondary-fg" :
    "border-subtle/60 text-muted-fg";
  return (
    <div
      className={`shrink-0 px-1.5 py-0.5 rounded-sm border text-[9px] font-mono uppercase tracking-widest tabular-nums ${tone}`}
      title={`Signal first detected: ${new Date(detectedAt).toLocaleString("en-IN")}`}
      data-testid="signal-age"
    >
      {label}
    </div>
  );
}

/**
 * Detect "trend reversal caution" — today's bounce against a multi-day
 * downtrend. Uses the row's sparkline (last 30 closes) to compute 1-week
 * and 1-month % change.
 *
 * Returns one of:
 *   - "bull_trap"   today > +1% but 1M < -3%  (counter-trend bounce in monthly down move)
 *   - "fading"      today < -1% but 1M > +5%  (sharp pullback in monthly up move — possibly buyable, but worth flagging)
 *   - null          no caution
 */
function detectCaution(item) {
  const today = item?.pct_change_today;
  const sp = item?.sparkline;
  if (typeof today !== "number" || !Array.isArray(sp) || sp.length < 6) return null;
  const last = sp[sp.length - 1];
  const ref1m = sp.length > 22 ? sp[sp.length - 1 - 22] : sp[0];
  const ref1w = sp.length > 5 ? sp[sp.length - 1 - 5] : sp[0];
  if (!last || !ref1m || !ref1w) return null;
  const m1 = ((last - ref1m) / ref1m) * 100;
  const w1 = ((last - ref1w) / ref1w) * 100;
  if (today > 1 && m1 < -3) {
    return { kind: "bull_trap", w1, m1, label: "Bull-trap risk", tooltip: `Today +${today.toFixed(1)}% but 1M ${m1.toFixed(1)}% · counter-trend bounce` };
  }
  if (today < -1 && m1 > 5) {
    return { kind: "fading", w1, m1, label: "Pullback", tooltip: `Today ${today.toFixed(1)}% in 1M +${m1.toFixed(1)}% uptrend · possible dip-buy zone` };
  }
  return null;
}

export default function StockRow({
  item,
  scoreKey = "score",
  scoreLabel = "",
  typeBadge,
  iconKind,
  onOpen,
  onWatch,
  watched = false,
  testIdPrefix = "row",
  rank,
  rightHint, // small "Today" / "12d ago" / status pill on the right
}) {
  const sym = (item.symbol || "").replace(".NS", "");
  const score = item[scoreKey];
  const today = item.pct_change_today;
  const KindIcon = iconKind ? KIND_ICON[iconKind] : null;
  const caution = detectCaution(item);

  const handleRowClick = (e) => {
    // Ignore clicks on inline interactive elements
    if (e.target.closest("button, a")) return;
    onOpen?.(item);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 2 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      role="button"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen?.(item);
        }
      }}
      data-testid={`${testIdPrefix}-${sym}`}
      className="group flex items-center gap-3 px-3 py-2.5 border-b border-subtle/60 hover:bg-surface-hover/60 hover:border-pos/30 transition cursor-pointer focus:outline-none focus-visible:bg-surface-hover/60"
    >
      {/* Rank (optional) */}
      {rank != null && (
        <div
          className="shrink-0 w-6 h-6 rounded-sm border border-subtle/80 flex items-center justify-center font-mono text-[10px] tabular-nums text-muted-fg"
          aria-label={`Rank ${rank}`}
        >
          {rank}
        </div>
      )}

      {/* Star (watch) */}
      {onWatch && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onWatch({ symbol: item.symbol });
          }}
          data-testid={`${testIdPrefix}-watch-${sym}`}
          title={watched ? "Remove from watchlist" : "Add to watchlist"}
          className={
            "shrink-0 p-1 rounded-sm transition " +
            (watched
              ? "text-pos"
              : "text-muted-fg/60 hover:text-pos")
          }
        >
          <Star size={13} weight={watched ? "fill" : "regular"} />
        </button>
      )}

      {/* Symbol + company */}
      <div className="min-w-0 flex-1 flex items-center gap-2">
        {KindIcon && <KindIcon size={11} weight="fill" className="text-pos shrink-0" />}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <a
              href={tradingViewStockUrl(item.symbol)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="font-mono text-sm font-semibold text-primary-fg hover:text-pos flex items-center gap-1 shrink-0"
              data-testid={`${testIdPrefix}-tv-${sym}`}
            >
              {sym}
              <ArrowSquareOut size={9} weight="bold" className="opacity-60" />
            </a>
            {score != null && (
              <span
                className={
                  "px-1 py-0 text-[9px] font-mono uppercase tracking-widest border rounded-sm shrink-0 " +
                  TONE_CLASS[scoreTone(score)]
                }
                title={`${scoreLabel || "Score"}: ${score}`}
              >
                {typeof score === "number" ? score.toFixed(1).replace(/\.0$/, "") : score}
                {scoreLabel ? `/10` : ""}
              </span>
            )}
            {typeBadge && (
              <span
                className={
                  "px-1 py-0 text-[9px] font-mono uppercase tracking-widest border rounded-sm shrink-0 " +
                  TONE_CLASS[typeBadge.tone || "muted"]
                }
              >
                {typeBadge.label}
              </span>
            )}
            {item.fundamental_rating && (
              <span
                className={
                  "px-1 py-0 text-[9px] font-mono uppercase tracking-widest border rounded-sm flex items-center gap-0.5 shrink-0 " +
                  (item.fundamental_rating === "Strong"
                    ? TONE_CLASS.pos
                    : item.fundamental_rating === "Moderate"
                    ? TONE_CLASS.mod
                    : item.fundamental_rating === "Weak"
                    ? TONE_CLASS.neg
                    : TONE_CLASS.muted)
                }
                title={`Fundamentals: ${item.fundamental_rating}`}
              >
                <Brain size={8} weight="fill" />
                {item.fundamental_rating}
              </span>
            )}
            {caution && (
              <span
                className={
                  "px-1 py-0 text-[9px] font-mono uppercase tracking-widest border rounded-sm flex items-center gap-0.5 shrink-0 " +
                  (caution.kind === "bull_trap"
                    ? "text-mod border-mod/60 bg-mod-soft/60"
                    : "text-pos border-pos/60 bg-pos-soft/60")
                }
                title={caution.tooltip}
                data-testid={`${testIdPrefix}-caution-${sym}`}
              >
                <Warning size={8} weight="fill" />
                {caution.kind === "bull_trap" ? "Bull-trap" : "Pullback"}
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-fg truncate max-w-[18rem]">
            {item.company_name || sym}
          </div>
          {item.noteText && (
            <div
              className="text-[10px] text-secondary-fg italic truncate max-w-[28rem] mt-0.5 flex items-center gap-1"
              title={item.noteText}
              data-testid={`${testIdPrefix}-note-${sym}`}
            >
              <span className="text-pos shrink-0">✎</span>
              {item.noteText}
            </div>
          )}
        </div>
      </div>

      {/* Price · Today% */}
      <div className="hidden md:flex flex-col items-end min-w-[5.5rem] shrink-0">
        <div className="font-mono text-sm tabular-nums text-primary-fg">
          ₹{fmt(item.last_price)}
        </div>
        {typeof today === "number" && (
          <div
            className={
              "text-[10px] font-mono tabular-nums " +
              (today >= 0 ? "text-pos" : "text-neg")
            }
          >
            {today >= 0 ? "+" : ""}
            {today.toFixed(2)}%
          </div>
        )}
      </div>

      {/* Entry · SL · Target */}
      <div className="hidden lg:flex items-center gap-3 font-mono text-[10px] tabular-nums shrink-0">
        <div className="flex flex-col items-end min-w-[3.5rem]">
          <span className="text-[8px] uppercase tracking-widest text-muted-fg">Entry</span>
          <span className="text-secondary-fg">₹{fmt(item.entry)}</span>
        </div>
        <div className="flex flex-col items-end min-w-[3.5rem]">
          <span className="text-[8px] uppercase tracking-widest text-muted-fg">SL</span>
          <span className="text-neg">₹{fmt(item.stop_loss ?? item.sl)}</span>
        </div>
        <div className="flex flex-col items-end min-w-[3.5rem]">
          <span className="text-[8px] uppercase tracking-widest text-muted-fg">Target</span>
          <span className="text-pos">₹{fmt(item.target)}</span>
        </div>
      </div>

      {/* Signal age — how old is this breakout? */}
      <SignalAgeBadge detectedAt={item.first_detected_at} />

      {/* Right hint */}
      {rightHint && (
        <div className="hidden sm:block shrink-0 text-[9px] font-mono uppercase tracking-widest text-muted-fg">
          {rightHint}
        </div>
      )}

      {/* Caret hint */}
      <CaretRight
        size={14}
        weight="bold"
        className="text-muted-fg/60 group-hover:text-pos transition shrink-0"
      />
    </motion.div>
  );
}
